// FeedbackContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_GET_TASK_TEMPLATE_SELECT,
  SG_FETCH_TASK_SCHEDULE,
  CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD,
  CLEAR_ADDED_FILE
} from "constants/actions";

/**
 * Parses a pulse survey object (like the one provided) and returns a
 * structured feedback string.
 *
 * @param {Object} pulseSurvey - The entire pulse survey object.
 * @returns {String} A formatted string of user feedback.
 */
function parsePulseSurveyResponses(pulseSurvey) {
  // Safety check: if no task_responses, return early.
  if (!pulseSurvey?.task_responses || pulseSurvey.task_responses.length === 0) {
    return "No task responses found.";
  }

  let feedbackString = "";

  pulseSurvey.task_responses.forEach((responseObj) => {
    // Each responseObj corresponds to one user (or one response session).
    // We can pull out 'employee' or 'email' if we want to note it in our feedback as well.
    const { task_response, employee } = responseObj;
    const userEmail = task_response?.email;

    // If you want to identify which user's feedback you are reporting:
    //   feedbackString += `=== Feedback from user ID ${employee} (${userEmail}) ===\n\n`;

    // The main array of question-response pairs is in task_list
    const taskList = task_response?.task_list;
    if (!taskList || taskList.length === 0) {
      feedbackString += "No questions answered.\n\n";
      return; // proceed to next responseObj
    }

    // Loop each question/response in the task_list
    taskList.forEach((item) => {
      const { question, response, follow_up } = item;

      if (typeof response === "number") {
        // This is presumably a "quantitative" question
        feedbackString += `User was asked quantitative question: ${question}\n\n`;
        feedbackString += `User scored this question: ${response}\n\n`;

        // If there's a follow-up for the quantitative question
        if (follow_up && follow_up.question && follow_up.response) {
          feedbackString += `User was asked follow-up question: ${follow_up.question}\n`;
          feedbackString += `User responded to the follow-up question with: ${follow_up.response}\n\n`;
        }
      } else {
        // This is presumably an "open text" question
        feedbackString += `User provided feedback for the following question: ${question}\n\n`;
        feedbackString += `User responded with: ${response}\n\n`;

        // If there's a follow-up for the open-text question
        if (follow_up && follow_up.question && follow_up.response) {
          feedbackString += `User was asked follow-up question: ${follow_up.question}\n`;
          feedbackString += `User responded to the follow-up question with: ${follow_up.response}\n\n`;
        }
      }
    });

    //   feedbackString += `=== End of feedback from user ID ${employee} ===\n\n`;
  });

  return feedbackString.trim();
}

// Helper function to calculate factor average
const calculateFactorAverage = (questions) => {
  let total = 0;
  let count = 0;

  questions.forEach((question) => {
    let response = question.response;
    if (question.reverse) {
      // Invert the score if the reverse is true
      response = 10 - response;
    }
    total += response;
    count++;
  });

  // Avoid division by zero
  const average = count > 0 ? (total / count) * 10 : 0;
  return average.toFixed(2); // Return percentage formatted to two decimal places
};

export const FeedbackContext = createContext();

export const useFeedbackContext = () => useContext(FeedbackContext);

export const FeedbackProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [feedback, setFeedback] = useState("");
  const [feedbackLength, setFeedbackLength] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [emptyData, setEmptyData] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [selectedPulse, setSelectedPulse] = useState(null);
  const [pulseData, setPulseData] = useState(null);
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [factorCardData, setFactorCardData] = useState([]);
  const [selectedPulses, setSelectedPulses] = useState([]);
  const [outcomeCardData, setOutcomeCardData] = useState([]);
  const [filteredPulses, setFilteredPulses] = useState([]);
  const [selectedCustomQuestions, setSelectedCustomQuestions] = useState([]);
  const [customQuestionsCardData, setCustomQuestionsCardData] = useState([]);
  const filteredData = useSelector((state) => state.audit?.filtered_data);
  const coreData = useSelector((state) => state.audit?.core_data);
  const questionStructure = useSelector(
    (state) => state.audit?.survey_version,
    shallowEqual
  );

  const get_auth = useSelector((state) => state.auth);

  const fetchedPulse = useSelector(
    (state) => state.task_templates?.fetched_scheduled_task
  );

  const pulseSurveys = useSelector(
    (state) => state.task_templates.scheduled_tasks?.results
  );

  useEffect(() => {
    dispatch({ type: SG_GET_TASK_TEMPLATE_SELECT });
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });
  }, [dispatch]);

  const factorLabels = React.useMemo(() => {
    if (!questionStructure) return [];

    let idCounter = 1;

    // For each dimension, build a structure that includes
    // dimension info plus an array of its factors.
    return (
      questionStructure.questions.dimensions?.map((dim, dimensionIndex) => {
        return {
          dimensionIndex,
          dimensionTitle: dim.title, // only if you want to include the dimension's title
          factors: dim.factors?.map((factor, factorIndex) => ({
            title: factor.title,
            level: 2,
            id: idCounter++,
            dimension: dimensionIndex,
            factor: factorIndex,
          })),
        };
      }) || []
    ); // fallback to empty array if no dimensions
  }, [questionStructure]);

  // Function to get custom questions from the survey structure
  const getCustomQuestions = React.useMemo(() => {
    if (!questionStructure?.questions?.comments) return [];

    return questionStructure.questions.comments.map((comment, index) => ({
      id: comment?.id || index,
      title: comment.question,
      level: 4,
      type: "comments",
      question: comment.question
    }));
  }, [questionStructure]);

  useEffect(() => {
    // Add the type: 'factor' to factorCardData
    setFactorCardData(
      selectedFactors?.map((factor) => ({
        ...factor,
        type: "culture",
      }))
    );
  }, [selectedFactors]);

  useEffect(() => {
    if (Array.isArray(selectedCustomQuestions[0])) {
      setCustomQuestionsCardData(
        [{
          type: "custom",
          id: "custom-questions", // Ensure id is a string as expected by DataSourceCard
          title: `Custom Questions (${selectedCustomQuestions[0].length})`, // Show count of selected questions
          selections: selectedCustomQuestions[0],
          count: selectedCustomQuestions[0].length
        }]
      );
    } else if (selectedCustomQuestions && selectedCustomQuestions.length > 0) {
      // Add the type: 'custom' to customQuestionsCardData and ensure proper structure
      setCustomQuestionsCardData(
        [{
          type: "custom",
          id: "custom-questions", // Ensure id is a string as expected by DataSourceCard
          title: `Custom Questions (${selectedCustomQuestions.length})`, // Show count of selected questions
          selections: selectedCustomQuestions,
          count: selectedCustomQuestions.length
        }]
      );
      // Sometimes for some reason, selectedCustomQuestions is an array of arrays.
      // We need to flatten it.
    } else {
      setCustomQuestionsCardData([]);
    }
  }, [selectedCustomQuestions]);

  useEffect(() => {
    if (pulseSurveys?.length > 0) {
      // Filter pulses based on user access
      // if the user created the pulse, show it.
      // If the users id is in the employee_access array, show it.
      // Get the user's id from the coreData?.permissions?.id
      const userCreatedPulses = pulseSurveys.filter(pulse => pulse.created_by === get_auth?.employee?.id);
      const userAccessedPulses = pulseSurveys.filter(pulse => pulse.employee_access.includes(get_auth?.employee?.id));
      setFilteredPulses([...userCreatedPulses, ...userAccessedPulses]);
    }

  }, [pulseSurveys, get_auth])

  const handleRemoveFactor = (factor) => {
    // Remove the factor from the selectedFactors array.
    // The factor object should be removed by matching factor.id, factor.dimension, and factor.factor.
    setSelectedFactors(
      selectedFactors.filter(
        (f) =>
          f.id !== factor.id ||
          f.dimension !== factor.dimension ||
          f.factor !== factor.factor
      )
    );
  };

  const handleRemoveAllFactors = () => {
    setSelectedFactors([]);
  };

  const handleRemoveOutcome = (outcome) => {
    setSelectedOutcomes(selectedOutcomes.filter((o) => o.id !== outcome.id));
  };

  const handleRemoveAllOutcomes = () => {
    setSelectedOutcomes([]);
  };

  const handleRemoveCustomQuestion = (question) => {
    // Ensure we're working with a proper array
    const questionsArray = Array.isArray(selectedCustomQuestions) ?
      [...selectedCustomQuestions] :
      Object.values(selectedCustomQuestions || {}).filter(item => typeof item === 'object');

    // Filter out the question to remove
    const updatedQuestions = questionsArray.filter((q) => {
      // Handle both array and object structures
      if (Array.isArray(q)) {
        return !q.some(item => item.id.toString() === question.id.toString());
      }
      return q.id.toString() !== question.id.toString();
    });

    setSelectedCustomQuestions(updatedQuestions);
  };

  const handleRemoveAllCustomQuestions = () => {
    // This is just like removing a pulse.
    // We need to remove the custom questions from the selectedCustomQuestions array.
    // We also need to remove the custom questions from the customQuestionsCardData array.
    setSelectedCustomQuestions([]);
    setCustomQuestionsCardData([]);
  };

  useEffect(() => {
    // Add the type: 'outcome' to outcomeCardData
    setOutcomeCardData(
      selectedOutcomes?.map((outcome) => ({
        ...outcome,
        type: "outcome",
      }))
    );
  }, [selectedOutcomes]);

  const generateDefaultGroupStructure = (
    outcomeQ,
    isFrench = false,
    pillarOnly = false
  ) => {
    if (!questionStructure) return [];

    let idCounter = 0;
    const defaultGroupStructure = [];

    // Overall Culture
    if (!pillarOnly) {
      defaultGroupStructure.push({
        title: isFrench ? "Culture Générale" : "Overall Culture",
        level: 0,
        id: -1,
      });
    }

    // Dimensions & Factors
    questionStructure.questions.dimensions?.forEach((dim, dimensionIndex) => {
      if (!pillarOnly) {
        defaultGroupStructure.push({
          title: isFrench ? dim.fr || dim.title : dim.title,
          level: 1,
          id: idCounter++,
          dimension: dimensionIndex,
        });
      }

      dim.factors?.forEach((factor, factorIndex) => {
        defaultGroupStructure.push({
          title: isFrench ? factor.fr || factor.title : factor.title,
          level: 2,
          id: idCounter++,
          dimension: dimensionIndex,
          factor: factorIndex,
        });
      });
    });

    // Outcomes
    if (outcomeQ?.questions?.length > 0) {
      defaultGroupStructure.push({
        title: "Outcomes",
        level: 1,
      });
      outcomeQ.questions.forEach((oq) => {
        defaultGroupStructure.push({
          title: oq.name,
          id: oq.id,
          level: 3,
          type: "outcome",
        });
      });
    }

    // Custom Questions
    if (questionStructure?.questions?.comments?.length > 0) {
      defaultGroupStructure.push({
        title: "Custom Questions",
        level: 1,
      });
      questionStructure.questions.comments.forEach((c, i) => {
        defaultGroupStructure.push({
          title: c.question,
          id: c?.id || i,
          level: 4,
          type: "comments",
        });
      });
    }

    return defaultGroupStructure;
  };

  // Helper to determine the group list
  const getGroupList = (
    selectedGroup,
    defaultGroupStructure,
    groupStructure,
    isOTP
  ) => {
    if (
      selectedGroup.length > 0 &&
      selectedGroup[0]?.title !== "Overall Culture" &&
      selectedGroup[0]?.id !== -1
    ) {
      return selectedGroup;
    }
    return isOTP ? defaultGroupStructure : [];
  };

  const handleSelectPulse = (pulseId) => {
    // If the pulse is already selected, return;
    // If the pulse is not selected, add it to the selectedPulses array.
    // The object should have the { id, type } of the pulse.
    if (selectedPulses.some((p) => p.id === pulseId)) return;
    // Find the pulse so we can get the title.
    const pulse = pulseSurveys.find((p) => p.id === pulseId);
    setSelectedPulses([
      // ...selectedPulses, limit to 1 pulse
      { id: pulseId, type: "pulse", title: pulse?.name || "N/A" },
    ]);

    // Get the feedback for the pulse, and determine
    // if there is more than 2 responses.
    // We need to call the API to get the feedback for the pulse.
    dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: pulseId });
  };

  const handleSelectCustomQuestion = (questionObj) => {
    // If the question is already selected, return
    if (selectedCustomQuestions?.some((q) => q?.id?.toString() === questionObj?.id?.toString())) return;

    // Ensure we're always working with an array
    const currentQuestions = Array.isArray(selectedCustomQuestions) ?
      [...selectedCustomQuestions] :
      Object.values(selectedCustomQuestions || {}).filter(item => typeof item === 'object');

    // Add the new question to the array
    setSelectedCustomQuestions([...currentQuestions, questionObj]);
  };

  useEffect(() => {
    if (fetchedPulse) {
      // Check the length of fetchedPulse?.task_responses
      if (fetchedPulse?.task_responses?.length > 2) {
        // If the user has selected an outcome or factor,
        // We need to add the length of the task_responses to the feedback length.
        const length = fetchedPulse?.task_responses?.length;
        const currentLength = feedbackLength;
        if (selectedOutcomes.length > 0 || selectedFactors.length > 0 || selectedCustomQuestions.length > 0) {
          setFeedbackLength(length + currentLength);
        } else {
          setFeedbackLength(length);
        }
      }
    }
  }, [fetchedPulse])

  const handleRemovePulse = (pulseId) => {
    // Remove the pulse from selectedPulses
    setSelectedPulses(selectedPulses.filter((p) => p.id !== pulseId));

    // Reset feedback length if no pulses are selected
    if (selectedPulses.length === 1) { // If this was the last pulse
      setFeedbackLength(0);
    }

    // Reset feedback if no pulses are selected
    if (selectedPulses.length === 1) { // If this was the last pulse
      setFeedback("");
    }
  };

  // Helper to filter feedback and questions by group level
  const filterByGroupLevel = (group, feedback, questions) => {
    let filteredFeedback = [...feedback];
    let filteredQuestions = [...questions];

    switch (group.level) {
      case 1:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            f.feedback.id === group.dimension ||
            f.feedback?.dimension === group.dimension
        );
        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some((question) => question.id === group.dimension)
        );
        break;
      case 2:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            (f.feedback.id === group.dimension ||
              f.feedback?.dimension === group.dimension) &&
            f.feedback.factor === group.factor
        );
        filteredQuestions = filteredQuestions.map((q) => ({
          ...q,
          questions: q.questions.filter(
            (question) =>
              question.id === group.dimension &&
              question.factor === group.factor
          ),
        }));
        break;
      case 3:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            f.type === "outcome" && Number(f.feedback.q) === Number(group.id)
        );
        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some(
            (question) => Number(question.q) === Number(group.id)
          )
        );
        break;
      case 4:
        // Handle custom questions (level 4)
        filteredFeedback = filteredFeedback.filter(
          (f) => f.type === "comments" && f.id === group.id
        );

        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some((question) => question.q === group.id)
        );
        break;
      default:
        break;
    }

    return { filteredFeedback, filteredQuestions };
  };

  const newChatClicked = () => {
    // If the user clicks on a new chat, we need to reset the feedback data.
    setFeedback("");
    setFeedbackLength(0);
    setFeedbackLoading(true);
    setEmptyData(false);
    setInitialLoad(false);
    // Clear the selected factors and outcomes
    setSelectedFactors([]);
    setSelectedOutcomes([]);
    setSelectedPulses([]);
    setSelectedCustomQuestions([]);
    setOutcomeCardData([]);
    setFactorCardData([]);
    setCustomQuestionsCardData([]);
    // Clear all file upload related state
    dispatch({ type: CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD });
    dispatch({ type: CLEAR_ADDED_FILE });
  };

  // Helper to generate feedback text
  const generateFeedbackText = (groupFeedback, isOTP, coreData) => {
    let feedbackText = "";
    groupFeedback.forEach((item) => {
      if (!isOTP && item?.type === "comments") {
        feedbackText += `Question: ${
          coreData?.questions?.comments?.[item?.id]?.question
        }\n`;
        feedbackText += `User feedback: ${item?.feedback?.feedback}\n`;
      } else if (isOTP && item.type === "comments") {
        feedbackText += `Question: ${item.feedback.question}\n`;
        feedbackText += `User feedback: ${item.feedback.response}\n`;
      } else if (
        !Number.isInteger(item.feedback.response) &&
        item?.type !== "outcome"
      ) {
        // Skip non-integer responses unless it's an outcome type
        return;
      } else if ("prompt_feedback" in item.feedback) {
        feedbackText += `Follow-up question: ${item.feedback.prompt_question}\n`;
        feedbackText += `User feedback: ${item.feedback.prompt_feedback}\n`;
      } else if (
        "feedback" in item.feedback &&
        item?.feedback?.feedback?.length > 0
      ) {
        feedbackText += `User feedback: ${item.feedback.feedback}\n`;
      }

      if (item.feedback.follow_up) {
        feedbackText += `Follow-up: ${item.feedback.follow_up}\n`;
      }
      if (item.feedback?.response && item.type === "outcome") {
        feedbackText += `Outcome response: ${item.feedback.response}\n`;
      }

      feedbackText += "\n";
    });

    return feedbackText;
  };

  const appendFeedback = (
    originalFeedbackData = [],
    originalQuestionData = [],
    metaTag = "",
    options = {}
  ) => {
    const { selectedGroup = [], groupStructure = [], isOTP = false } = options;

    let allFilteredQuestions = [];
    let allFilteredFeedback = [];
    let feed = "";

    // Add initial feedback header if we have selected level 4
    if (selectedGroup.some(group => group.level === 4)) {
      feed += "The following feedback are responses to the question: " +
        selectedGroup.find(group => group.level === 4)?.title + "\n\n";
    }

    // Combine the selectedFactors, selectedOutcomes, and selectedCustomQuestions
    const selectedAnalysis = [
      ...selectedFactors,
      ...selectedOutcomes.map((outcome) => ({
        title: outcome.title,
        id: outcome.id,
        level: 3,
        type: "outcome",
      })),
      ...(Array.isArray(selectedCustomQuestions[0])
        ? selectedCustomQuestions[0].map((question) => ({
          title: question.title,
          id: question?.id,
          level: 4,
          type: "comments",
        }))
        : selectedCustomQuestions.map((question) => ({
          title: question.title,
          id: question?.id,
          level: 4,
          type: "comments",
        }))
      ),
    ];

    const defaultGroupStructure =
      selectedAnalysis?.length > 0
        ? selectedAnalysis
        : generateDefaultGroupStructure(coreData?.outcomeQuestions);

    const groupList = getGroupList(
      selectedAnalysis,
      defaultGroupStructure,
      defaultGroupStructure,
      isOTP
    );

    let totalFilteredLength = 0;

    groupList.forEach((group) => {
      const { filteredFeedback, filteredQuestions } = filterByGroupLevel(
        group,
        originalFeedbackData,
        originalQuestionData
      );

      // Only process if we have feedback or questions
      if (filteredFeedback.length > 0 || filteredQuestions.length > 0) {
        // Calculate factor average
        const factorAverage = calculateFactorAverage(
          filteredQuestions.flatMap((q) => q.questions)
        );

        // Append group-level feedback
        feed += `### ${group.title} ${metaTag} - ${group.title} - scored - ${factorAverage}%:\n\n`;

        // Generate feedback text
        feed += generateFeedbackText(filteredFeedback, isOTP, coreData);

        feed += `### End of ${group.title} feedback\n\n`;

        allFilteredFeedback.push(...filteredFeedback);
        allFilteredQuestions.push(...filteredQuestions);
        totalFilteredLength += filteredFeedback.length;
      }
    });

    return { feed, totalFilteredLength };
  };

  const getQuestionResponses = () => {
    let _data = [];
    let filtered = filteredData?.[0]?.[0] ? filteredData[0] : filteredData;

    filtered
      ?.filter((f) => "questions" in f) // Check if 'questions' exists
      ?.filter((f) => f?.questions?.length > 0) // Ensure questions have data
      ?.map((item) => {
        // Push object with questions and categories
        _data.push({
          questions: item.questions, // Store questions
          categories: item.categories, // Include categories for context
        });
      });

    return _data;
  };

  const getFeedbackContext = () => {
    let _data = [];
    let filtered = filteredData?.[0]?.[0] ? filteredData[0] : filteredData;

    if (!initialLoad && filtered?.length > 0) setInitialLoad(true);
    if (filtered?.length === 0) setEmptyData(true);
    else setEmptyData(false);

    // Handle regular feedback
    filtered
      ?.filter((item) => item.feedback?.length > 0)
      .forEach((item) =>
        item.feedback.forEach((f) =>
          _data.push({ feedback: f, categories: item.categories })
        )
      );

    // Handle feedback_builder responses
    filtered
      ?.filter((f) => "feedback_builder" in f)
      ?.filter((f) => f.feedback_builder?.responses?.length > 0)
      ?.forEach((item) => {
        item.feedback_builder.responses
          ?.filter((f) => f?.response)
          ?.forEach((f) => {
            _data.push({
              feedback: f?.responses || f,
              categories: item.categories,
              type: "outcome",
            });
          });
      });

    // Handle comments
    filtered
      ?.filter((f) => "comments" in f)
      ?.filter((f) => f.comments?.length > 0)
      ?.forEach((item) => {

        const comments = item.comments
          .filter((f) => {
            if (f?.feedback) {
              return f.feedback;
            } else if (f?.response) {
              return f?.response;
            }
          });

        const qData = questionStructure?.questions?.comments || [];

        comments.forEach((f, index) => {
          let correspondingQuestion = qData[index]; // Get the corresponding question from qData
          if (comments.length != qData.length) {
            correspondingQuestion = qData.find(q => q?.question?.toLowerCase()?.includes(f?.question?.toLowerCase()));
          }

          _data.push({
            feedback: f,
            categories: item.categories,
            type: "comments",
            id: correspondingQuestion ? correspondingQuestion.id || index : index,
          });
        });
      });

    return _data;
  };

  useEffect(() => {
    if (!filteredData || !coreData) return;
    const contextData = getFeedbackContext();
    const questionData = getQuestionResponses();
    
    // Append feedback using the function and get the filtered feedback length
    const { feed: compiledFeedback, totalFilteredLength } = appendFeedback(contextData, questionData);

    setFeedback(compiledFeedback);

    if (selectedPulses.length > 0) {
      // If the user has selected a pulse, make sure the feedback length isn't completely removed.
      if (totalFilteredLength === 0) {
        setFeedbackLength(fetchedPulse?.task_responses?.length || 0);
      } else {
        // If the user has selected an outcome or factor or custom question,
        // We need to add the length of the task_responses to the feedback length.
        const pulseLength = fetchedPulse?.task_responses?.length || 0;
        // Use the higher value between pulse length and current filtered length
        setFeedbackLength(Math.max(pulseLength, totalFilteredLength));
      }
    } else {
      setFeedbackLength(totalFilteredLength);
    }

    setFeedbackLoading(false);
  }, [
    filteredData,
    coreData,
    questionStructure,
    selectedFactors,
    selectedOutcomes,
    selectedPulses,
    selectedCustomQuestions,
  ]);


  // ----------------------------------
  // Expose a param-builder for the Chat
  // ----------------------------------
  // If you'd like to add more fields (like "tags"), you can do so,
  // or pull them from another context if needed.
  const getFeedbackParams = (fileSources = [], promptObject) => {
    const metadata =
      fileSources?.length > 0
        ? fileSources?.map((source) => ({
            id: source.id,
            type: "file",
            name: source.name,
          }))
        : [];

    const tags = selectedFactors?.map((factor) => factor.title);
    // Add outcomes to the tags
    const outcomes = selectedOutcomes?.map((outcome) => outcome.title);
    const pulseData =
      selectedPulses?.map((pulse) => ({
        id: pulse.id,
        type: "pulse",
        title: pulse.title,
      })) || [];


    // Add custom questions to params
    const customQuestions = selectedCustomQuestions?.map((question) => question.title);

    const promptData = promptObject
      ? {
          id: promptObject?.id,
          type: "prompt",
          question: promptObject?.categoryContent?.props?.prompt?.ques,
          prompt: promptObject?.categoryContent?.props?.prompt?.prompt,
          title: promptObject?.categoryTitle,
        }
      : {};

    const dataSources = [...pulseData, ...metadata, promptData];
    return {
      feedback_length: feedbackLength,
      date: "",
      // This is an expected param, do not overwrite
      context_data_length: 0,
      // If you do store 'tags' or 'factors' here, you can add them:
      tags,
      dataSources,
      outcomes,
      customQuestions,
    };
  };

  const handleOutcomeSelection = (outcomeId) => {
    const selectedOutcome = outcomeCardData.find((o) => o.id === outcomeId);
    if (selectedOutcome) {
      setOutcomeCardData(
        outcomeCardData.map((o) =>
          o.id === outcomeId ? { ...o, selected: !o.selected } : o
        )
      );
    }
  };


  return (
    <FeedbackContext.Provider
      value={{
        feedback,
        feedbackLength,
        feedbackLoading,
        emptyData,
        initialLoad,
        getFeedbackParams,
        fetchedPulse,
        pulseSurveys: filteredPulses,
        setSelectedPulse,
        pulseData,
        outcomeQ: coreData?.outcomeQuestions,
        factorLabels,
        setSelectedFactors,
        setSelectedOutcomes,
        handleSelectPulse,
        handleRemovePulse,
        selectedPulses,
        selectedFactors,
        selectedOutcomes,
        factorCardData,
        outcomeCardData,
        handleRemoveFactor,
        handleRemoveOutcome,
        newChatClicked,
        handleRemoveAllFactors,
        handleRemoveAllOutcomes,
        customQuestions: getCustomQuestions,
        selectedCustomQuestions,
        setSelectedCustomQuestions,
        handleSelectCustomQuestion,
        handleRemoveCustomQuestion,
        handleRemoveAllCustomQuestions,
        customQuestionsCardData
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
};
